import Cryptographer from '../data/cryptographer';

/**
 * Singleton service for managing Cryptographer instances for multiple vaults
 * Maintains the crypto instances outside of Redux state while keeping them tied to their vaults
 */
class CryptographerService {
  private static instance: CryptographerService;
  private cryptographers: Map<string, Cryptographer> = new Map();

  private constructor() {}

  static getInstance(): CryptographerService {
    if (!CryptographerService.instance) {
      CryptographerService.instance = new CryptographerService();
    }
    return CryptographerService.instance;
  }

  /**
   * Set the cryptographer for a specific vault
   */
  async setCryptographer(vaultId: string, password: string): Promise<void> {
    const cryptographer = await Cryptographer.create(password);
    this.cryptographers.set(vaultId, cryptographer);
  }

  /**
   * Get the cryptographer for a specific vault
   * Returns null if no cryptographer is set for this vault
   */
  getCryptographer(vaultId: string): Cryptographer | null {
    return this.cryptographers.get(vaultId) ?? null;
  }

  /**
   * Check if cryptographer is available for the given vault
   */
  hasCryptographer(vaultId: string): boolean {
    return this.cryptographers.has(vaultId);
  }

  /**
   * Clear the cryptographer for a specific vault
   */
  clearCryptographer(vaultId: string): void {
    this.cryptographers.delete(vaultId);
  }

  /**
   * Clear all cryptographers (e.g., when logging out completely)
   */
  clearAllCryptographers(): void {
    this.cryptographers.clear();
  }

  /**
   * Get all vault IDs that have active cryptographers
   */
  getOpenVaultIds(): string[] {
    return Array.from(this.cryptographers.keys());
  }

  /**
   * Get the number of open vaults
   */
  getOpenVaultCount(): number {
    return this.cryptographers.size;
  }
}

// Export singleton instance
export const cryptographerService = CryptographerService.getInstance();
export default cryptographerService;